import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { AdminRecordModal, AdminModalFooter } from "../../components/admin/AdminRecordModal";
import { toast } from "sonner";
import {
  Plus,
  PencilSimple,
  ArrowUp,
  ArrowDown,
  Check,
  X,
  ListBullets,
} from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Standalone page component - redirects to System Utilities with service-types tab
export default function AdminServiceTypes() {
  return <Navigate to="/admin/system?tab=service-types" replace />;
}

// Reusable Service Types Panel for embedding in System Utilities
export function ServiceTypesPanel() {
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({ name: "", sort_order: 0 });

  useEffect(() => {
    fetchServiceTypes();
  }, []);

  const fetchServiceTypes = async () => {
    try {
      const response = await fetch(`${API}/admin/service-types`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setServiceTypes(data.service_types || []);
      } else {
        toast.error("Failed to fetch service types");
      }
    } catch (error) {
      console.error("Error fetching service types:", error);
      toast.error("Failed to fetch service types");
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingType(null);
    setFormData({ name: "", sort_order: serviceTypes.length });
    setModalOpen(true);
  };

  const openEditModal = (serviceType) => {
    setEditingType(serviceType);
    setFormData({ name: serviceType.name, sort_order: serviceType.sort_order });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return;
    }

    try {
      const url = editingType
        ? `${API}/admin/service-types/${editingType.service_type_id}`
        : `${API}/admin/service-types`;
      const method = editingType ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success(editingType ? "Service type updated" : "Service type created");
        setModalOpen(false);
        fetchServiceTypes();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save service type");
      }
    } catch (error) {
      console.error("Error saving service type:", error);
      toast.error("Failed to save service type");
    }
  };

  const toggleActive = async (serviceType) => {
    try {
      const endpoint = serviceType.is_active ? "deactivate" : "reactivate";
      const response = await fetch(
        `${API}/admin/service-types/${serviceType.service_type_id}/${endpoint}`,
        {
          method: "POST",
          credentials: "include",
        }
      );

      if (response.ok) {
        toast.success(`Service type ${serviceType.is_active ? "deactivated" : "reactivated"}`);
        fetchServiceTypes();
      } else {
        toast.error("Failed to update service type");
      }
    } catch (error) {
      console.error("Error toggling service type:", error);
      toast.error("Failed to update service type");
    }
  };

  const moveItem = async (index, direction) => {
    const newOrder = [...serviceTypes];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newOrder.length) return;
    
    [newOrder[index], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[index]];
    
    try {
      const response = await fetch(`${API}/admin/service-types/reorder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          service_type_ids: newOrder.map(t => t.service_type_id),
        }),
      });

      if (response.ok) {
        setServiceTypes(newOrder);
        toast.success("Order updated");
      } else {
        toast.error("Failed to update order");
      }
    } catch (error) {
      console.error("Error reordering:", error);
      toast.error("Failed to update order");
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {[...Array(2)].map((_, i) => (
          <div key={i} className="h-32 bg-zinc-700 rounded animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-service-types-panel">
      {/* Service Types Card */}
      <Card className="bg-zinc-800 border-zinc-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                <ListBullets size={20} />
                Service Types Configuration
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Manage the service types available for contractors when creating jobs
              </CardDescription>
            </div>
            <Button
              onClick={openCreateModal}
              className="bg-[#FF6600] hover:bg-[#CC5200] text-white gap-2"
              data-testid="create-service-type-btn"
            >
              <Plus size={18} weight="bold" />
              Add Service Type
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Service Types Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-700">
                  <th className="text-left py-3 px-4 text-zinc-400 font-medium w-24">Order</th>
                  <th className="text-left py-3 px-4 text-zinc-400 font-medium">Name</th>
                  <th className="text-left py-3 px-4 text-zinc-400 font-medium w-24">Status</th>
                  <th className="text-right py-3 px-4 text-zinc-400 font-medium w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                {serviceTypes.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center py-8 text-zinc-500">
                      No service types found. Add one to get started.
                    </td>
                  </tr>
                ) : (
                  serviceTypes.map((type, index) => (
                    <tr 
                      key={type.service_type_id} 
                      className={`border-b border-zinc-700/50 ${!type.is_active ? "opacity-50" : ""}`}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-zinc-400 hover:text-white hover:bg-zinc-700"
                            onClick={() => moveItem(index, "up")}
                            disabled={index === 0}
                          >
                            <ArrowUp size={14} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-zinc-400 hover:text-white hover:bg-zinc-700"
                            onClick={() => moveItem(index, "down")}
                            disabled={index === serviceTypes.length - 1}
                          >
                            <ArrowDown size={14} />
                          </Button>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-zinc-300 font-medium">{type.name}</td>
                      <td className="py-3 px-4">
                        <Badge
                          className={
                            type.is_active
                              ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                              : "bg-zinc-500/20 text-zinc-400 border-zinc-500/30"
                          }
                        >
                          {type.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-zinc-400 hover:text-white hover:bg-zinc-700"
                            onClick={() => openEditModal(type)}
                            data-testid={`edit-${type.slug}`}
                          >
                            <PencilSimple size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={`h-8 w-8 ${
                              type.is_active 
                                ? "text-red-400 hover:text-red-300 hover:bg-red-500/20" 
                                : "text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/20"
                            }`}
                            onClick={() => toggleActive(type)}
                            data-testid={`toggle-${type.slug}`}
                          >
                            {type.is_active ? <X size={16} /> : <Check size={16} />}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Modal - Dark themed */}
      <AdminRecordModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        title={editingType ? "Edit Service Type" : "Add Service Type"}
        size="sm"
        footer={
          <AdminModalFooter onCancel={() => setModalOpen(false)}>
            <Button
              onClick={handleSubmit}
              className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
              data-testid="save-service-type-btn"
            >
              {editingType ? "Update" : "Create"}
            </Button>
          </AdminModalFooter>
        }
      >
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-zinc-300">Name *</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Plumbing"
            className="bg-zinc-800 border-zinc-600 text-white placeholder:text-zinc-500"
            data-testid="service-type-name-input"
          />
        </div>
      </AdminRecordModal>
    </div>
  );
}
